"use client"

import React, { useEffect, useRef, useState, Fragment } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import {
  X, Send, MessageSquare, Bot, User, Phone, Mail, GraduationCap, MapPin,
  AlertCircle, Archive, Save, UserCircle, Calendar, Bell, Pencil, Trash2,
  ChevronLeft, ChevronRight, Download, FileText, PanelLeftClose, PanelLeftOpen,
  Ban, CheckCircle2, Sparkles, Baby, Users, Hash, BookOpen, ChevronDown, Loader2,
  MoreVertical, Copy, Check, Eye, ArrowRightLeft
} from "@/lib/icons"

import {
  getLeadMessages,
  sendLeadMessage,
  updateLead,
  supabase,
  subscribeToMessages,
  getAsesores,
  logLeadActivity,
  createTask,
  deleteLeadMessage,
  editLeadMessage,
  excludeWaContact
} from "@/lib/supabase"
import { sendWhatsAppMessage, sendWhatsAppDocument, sendWhatsAppImage, deleteWhatsAppMessage, editWhatsAppMessage } from "@/lib/n8n-api"
import { useWhatsAppProfilePic } from "@/hooks/useWhatsAppProfilePic"
import { saveConversationMessage } from "@/lib/supabase"
import { LeadChildrenSection } from "@/components/kanban/lead-children-section"
import { LeadActivityTimeline } from "@/components/kanban/lead-activity-timeline"
import { LeadTasksSection } from "@/components/kanban/lead-tasks-section"
import { LeadSourceBadge, getSourceTooltip, getSourceMeta, getSourceDetailLabel } from "@/components/kanban/lead-source-badge"
import { TransferLeadModal } from "@/components/kanban/transfer-lead-modal"

import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { AlertModal } from "@/components/ui/alert-modal"
import { ConfirmModal } from "@/components/ui/confirm-modal"
import { DateTimePicker } from "@/components/ui/date-time-picker"
import { LEAD_STAGE_META, normalizeLeadStage } from "@/lib/lead-stages"
import { LEVEL_META, normalizeLevel } from "@/components/kanban/lead-level-avatar"
import { getNivelLabel as getNivelLabelBase } from "@/lib/educational-levels"
import { getDayKey } from "@/lib/dates"

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://kgowevpumysppcwkbqxn.supabase.co"
const DOCS_BUCKET = "documentos-escolares"

// Catálogo oficial (orden de envío que usan las CAPs): brochure → modelo → horarios → actividades.
// Los informes por nivel y transporte quedaron desactualizados y se retiraron (2026-06-01).
const DOCUMENT_CATALOG = [
  { key: "brochure", label: "Brochure IT", fileName: "Brochure IT.pdf" },
  { key: "modelo", label: "Modelo Educativo", fileName: "Modelo Educativo Instituto Tepeyac.pdf" },
  { key: "horarios", label: "Horarios y Documentación", fileName: "Horarios y documentacion.pdf" },
  { key: "actividades", label: "Actividades Extracurriculares", fileName: "Actividades extracurriculares.pdf" },
]

const QUICK_REPLIES = [
  { emoji: "📅", label: "Agendar cita", text: "¡Hola! ¿Qué tal? Oye, me encantaría que vinieras a conocer el colegio, ¿cuándo se te haría bien para darte un recorrido? 😊" },
  { emoji: "📋", label: "Info nivel", text: "Oye, mira, te mando la info del nivel que te interesa para que le eches un ojito y cualquier duda me dices, ¿va? 👍" },
  { emoji: "💰", label: "Costos", text: "Claro, ahorita te paso la info de costos. Nada más dime qué nivel te interesa para mandarte los datos correctos 😊" },
  { emoji: "🎓", label: "Becas", text: "¡Sí tenemos becas! Te puedo pasar los requisitos. ¿Quieres que te mande la info para que la revises con calma?" },
  { emoji: "🕐", label: "Horarios", text: "Estamos de lunes a viernes de 8 a 2. ¿A qué hora se te facilita venir? Para apartarte un espacio 🙂" },
  { emoji: "💛", label: "Gracias", text: "¡Gracias a ti por el interés! Aquí andamos para lo que necesites, no dudes en escribirme 💛" },
  { emoji: "👋", label: "Seguimiento", text: "¡Hola! ¿Cómo estás? Oye, nada más quería saber si ya pudiste checar la info que te mandé. ¿Tienes alguna duda?" },
  { emoji: "✅", label: "Confirmar cita", text: "¡Perfecto, queda tu cita! Te esperamos. Si necesitas que te mande la ubicación o tienes alguna duda, avísame 📍" },
  { emoji: "📍", label: "Ubicación", text: "¡Sale! Aquí te paso la ubicación para que la guardes y llegues sin problema 🚗\n\n📍 IT Innovation School Guadalajara\nC. Ocampo 389, San Juan de Ocotán, 45019 Zapopan, Jal.\n\nhttps://maps.app.goo.gl/KirMbTAUuDPrY7zp7" },
  { emoji: "📞", label: "Te llamo", text: "Oye, ¿te parece si te marco para platicar mejor? Dime a qué hora te conviene y con mucho gusto te llamo 📞" },
]

const waHeaderBg = "#075e54"
// Colores por tipo de mensaje
const BUBBLE_COLORS = {
  cliente: { bg: "#ffffff", text: "#1a1a1a", label: null, labelColor: null, avatarBg: "#fdf2f4", avatarText: "#7a1b33", timeTxt: "#94a3b8", shadow: "0 1px 3px rgba(0,0,0,0.06)" },
  agent: { bg: "#fdf2f4", text: "#1a1a1a", label: "Asesor", labelColor: "#7a1b33", avatarBg: "#fce7eb", avatarIcon: "tinto", timeTxt: "#94a3b8", shadow: "0 1px 3px rgba(122,27,51,0.08)" },
  bot: { bg: "#f0fdf4", text: "#1a1a1a", label: "Bot IA", labelColor: "#166534", avatarBg: "#dcfce7", avatarIcon: "green", timeTxt: "#94a3b8", shadow: "0 1px 3px rgba(22,101,52,0.08)" },
}

const getNivelLabel = (nivel) => (nivel ? getNivelLabelBase(nivel) : "Sin nivel")

// Devuelve etiqueta legible: "Hoy", "Ayer" o "10 de mayo de 2026"
const formatDayLabel = (iso) => {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  const today = new Date()
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1)
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (sameDay(d, today)) return "Hoy"
  if (sameDay(d, yesterday)) return "Ayer"
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })
}

const getReminderCount = (leadId) => {
  try {
    const raw = localStorage.getItem(`reminders_${leadId}`)
    return Math.max(0, parseInt(raw || "0", 10))
  } catch { return 0 }
}

const incrementReminderCount = (leadId) => {
  try {
    const n = getReminderCount(leadId) + 1
    localStorage.setItem(`reminders_${leadId}`, String(n))
    return n
  } catch { return 0 }
}

export function LeadUnifiedView({ lead, user, open, onClose, onSave, onRequestArchive, onDelete }) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [text, setText] = useState("")
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [transferPreselected, setTransferPreselected] = useState("")
  const [formData, setFormData] = useState({
    ...lead,
    campus: lead?.campus_nombre || lead?.campus || "",
    asesor_nombre: lead?.asesor_nombre || lead?.asesor || "",
    estado: lead?.estado || "",
    municipio: lead?.municipio || "",
    nivel_educativo: (lead?.nivel_educativo || "").toLowerCase(),
    telefono: lead?.telefono || "",
    email: lead?.email || "",
    nombre_tutor: lead?.nombre_tutor || lead?.tutor || "",
    nombre_alumno: lead?.nombre_alumno || "",
    grado_especifico: lead?.grado_especifico || "",
    es_familia: lead?.es_familia ?? false,
    num_hijos: lead?.num_hijos ?? 1,
    tema_consulta: lead?.tema_consulta || ""
  })
  const [saving, setSaving] = useState(false)
  const [mobileTab, setMobileTab] = useState("chat")
  const [editingName, setEditingName] = useState(false)
  const [localName, setLocalName] = useState(lead?.nombre_tutor || "")
  const [reminderCount, setReminderCount] = useState(0)
  const [sendingReminder, setSendingReminder] = useState(false)
  const [showDocMenu, setShowDocMenu] = useState(false)
  const [sendingDoc, setSendingDoc] = useState(false)
  const [previewDoc, setPreviewDoc] = useState(null) // { label, fileName, url }
  const [pendingFile, setPendingFile] = useState(null)
  const [customCaption, setCustomCaption] = useState("")
  const [sendWithoutCaption, setSendWithoutCaption] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadStage, setUploadStage] = useState("")
  const [showQuickReplies, setShowQuickReplies] = useState(false)

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Mensaje a eliminar (modal estilo WhatsApp "eliminar para todos")
  const [messageToDelete, setMessageToDelete] = useState(null)
  const [deletingMessage, setDeletingMessage] = useState(false)

  // Menú kebab + edición inline
  const [openMenuId, setOpenMenuId] = useState(null)
  const [editingMessageId, setEditingMessageId] = useState(null)
  const [editingText, setEditingText] = useState("")
  const [savingEdit, setSavingEdit] = useState(false)
  const [copiedMessageId, setCopiedMessageId] = useState(null)

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [asesoresList, setAsesoresList] = useState([])
  const [activityReload, setActivityReload] = useState(0)
  const bumpActivity = () => setActivityReload((k) => k + 1)
  const [tasksReload, setTasksReload] = useState(0)
  const bumpTasks = () => setTasksReload((k) => k + 1)
  const [taskStats, setTaskStats] = useState({ pending: 0, overdue: 0, total: 0 })

  // WhatsApp profile picture (uses DB cache, fetches from API only once)
  const { picUrl: waProfilePic } = useWhatsAppProfilePic(lead)
  // Las URLs de pps.whatsapp.net expiran (403): si falla la imagen, mostramos la inicial
  const [avatarError, setAvatarError] = useState(false)
  useEffect(() => { setAvatarError(false) }, [waProfilePic])

  const scrollRef = useRef(null)
  const conversationCacheRef = useRef(new Map())
  const fileInputRef = useRef(null)
  // Marca si el próximo envío viene de una respuesta rápida,
  // para no duplicar el log (respuesta_rapida + mensaje_enviado)
  const fromQuickReplyRef = useRef(false)

  const normalizeHumanFlag = (v) => {
    if (v === true || v === 1 || v === "1") return true
    if (v === false || v === 0 || v === "0") return false
    if (typeof v === "string") return v.toLowerCase() === "true"
    return Boolean(v)
  }
  const [isHumanMode, setIsHumanMode] = useState(normalizeHumanFlag(lead?.intervencion_humana))
  const [toggling, setToggling] = useState(false)
  const canToggleMode = user?.rol === "asesor" || user?.isAdmin
  const wa_jid = lead?.wa_jid ?? (lead?.telefono ? `${lead.telefono}@s.whatsapp.net` : null)
  const resolvedInstance = (() => {
    const userEmail = (user?.email || "").toLowerCase().trim();
    const leadAsesorEmail = (lead?.asesor_email || "").toLowerCase().trim();
    if (userEmail === "gaby@tepeyac.mx") return "tepeyec-gaby";
    if (userEmail === "lulu@tepeyac.mx") return "tepeyact-produccion";
    if (leadAsesorEmail === "gaby@tepeyac.mx") return "tepeyec-gaby";
    if (leadAsesorEmail === "lulu@tepeyac.mx") return "tepeyact-produccion";
    return lead?.instancia || "tepeyact-produccion";
  })();

  const [alertModal, setAlertModal] = useState({ isOpen: false, title: "", message: "", type: "info" })
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: "", message: "", onConfirm: () => { }, type: "warning" })
  const showAlert = (title, message, type) => setAlertModal({ isOpen: true, title, message, type })
  const showConfirm = (title, message, onConfirm, type) => setConfirmModal({ isOpen: true, title, message, onConfirm, type })
  const closeAlert = () => setAlertModal((p) => ({ ...p, isOpen: false }))
  const closeConfirm = () => setConfirmModal((p) => ({ ...p, isOpen: false }))

  const scrollToBottom = (smooth = true) => {
    const el = scrollRef.current
    if (!el) return
    if (smooth) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
    else el.scrollTop = el.scrollHeight
  }
  const isUserNearBottom = (threshold = 200) => {
    const el = scrollRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
  }

  useEffect(() => { if (lead?.intervencion_humana !== undefined) setIsHumanMode(normalizeHumanFlag(lead.intervencion_humana)) }, [lead?.intervencion_humana])
  useEffect(() => {
    if (lead) {
      setFormData({
        ...lead,
        campus: lead.campus_nombre || lead.campus || "",
        asesor_nombre: lead.asesor_nombre || lead.asesor || "",
        estado: lead.estado || "",
        municipio: lead.municipio || "",
        nivel_educativo: (lead.nivel_educativo || "").toLowerCase(),
        telefono: lead.telefono || "",
        email: lead.email || "",
        nombre_tutor: lead.nombre_tutor || lead.tutor || "",
        nombre_alumno: lead.nombre_alumno || "",
        grado_especifico: lead.grado_especifico || "",
        es_familia: lead.es_familia ?? false,
        num_hijos: lead.num_hijos ?? 1,
        tema_consulta: lead.tema_consulta || ""
      });
      setLocalName(lead.nombre_tutor || "");
      setReminderCount(getReminderCount(lead.id))
    }
  }, [lead])

  useEffect(() => {
    if (!open) return
    getAsesores().then(({ data }) => { if (data?.length) setAsesoresList(data) })
  }, [open])

  useEffect(() => {
    if (!open || !lead?.id) return
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "" }
  }, [open, lead?.id])

  useEffect(() => {
    if (!open || !lead?.id) return
    let active = true
    const channelRef = { current: null }

    const loadConversation = async () => {
      setLoading(true)
      try {
        const cached = conversationCacheRef.current.get(lead.id)
        if (cached?.length) { setMessages(cached); setTimeout(() => scrollToBottom(false), 40) }
        const res = await getLeadMessages({ leadId: lead.id })
        const sanitized = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
        if (!active) return
        conversationCacheRef.current.set(lead.id, sanitized)
        setMessages(sanitized)
        setTimeout(() => scrollToBottom(false), 80)
      } catch (e) { console.error("loadConversation error", e) }
      finally { if (active) setLoading(false) }
    }
    loadConversation()

    const sortByDate = (arr) => [...arr].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : Infinity
      const tb = b.created_at ? new Date(b.created_at).getTime() : Infinity
      return ta - tb
    })

    const mergeIncoming = (incoming) => {
      if (!active || !incoming) return
      const shouldScroll = isUserNearBottom()
      setMessages((prev) => {
        if (prev.some((m) => m.id === incoming.id)) return prev
        const idx = prev.findIndex((m) => String(m.id).startsWith("tmp-") && m.mensaje?.trim() === incoming.mensaje?.trim())
        let next
        if (idx !== -1) {
          const u = [...prev]; u[idx] = incoming
          next = sortByDate(u)
        } else {
          next = sortByDate([...prev, incoming])
        }
        conversationCacheRef.current.set(lead.id, next)
        return next
      })
      if (shouldScroll) setTimeout(() => scrollToBottom(true), 80)
    }

    ; (async () => {
      try {
        const ch = await subscribeToMessages(lead.id, mergeIncoming)
        if (!active) { if (ch) supabase.removeChannel(ch); return }
        channelRef.current = ch
      } catch (e) { console.warn("Subscribe failed", e) }
    })()

    return () => {
      active = false
      if (channelRef.current) supabase.removeChannel(channelRef.current)
    }
  }, [open, lead?.id])

  useEffect(() => {
    if (!open || !lead?.id || !messages.length) return
    try {
      const last = messages[messages.length - 1]
      localStorage.setItem(`last_seen_${lead.id}`, last.created_at || new Date().toISOString())
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("leadConversationSeen", { detail: { leadId: lead.id } }))
    } catch { }
  }, [messages, open, lead?.id])

  const handleSend = async () => {
    if (!text.trim() || !isHumanMode) return
    const messageText = text.trim()
    setText("")
    setSending(true)
    const temp = { id: `tmp-${Date.now()}`, mensaje: messageText, tipo: "agent", created_at: new Date().toISOString(), wa_jid, sesion_id: wa_jid }
    setMessages((prev) => { const next = [...prev, temp]; conversationCacheRef.current.set(lead.id, next); return next })
    setTimeout(() => scrollToBottom(true), 50)
    try {
      await sendLeadMessage({ wa_jid, mensaje: messageText, tipo: "agent", leadId: lead.id, telefono: lead.telefono || null, isHumanMode: true, asesor_email: user?.email || null, instancia: resolvedInstance })
      toast.success("Mensaje enviado por WhatsApp")
      // Si el envío vino de una respuesta rápida, ya se logueó como "respuesta_rapida"
      // en handleQuickReply; no dupliques con un "mensaje_enviado".
      if (fromQuickReplyRef.current) {
        fromQuickReplyRef.current = false
      } else {
        logLeadActivity(lead.id, {
          tipo: "mensaje_enviado",
          titulo: "Mensaje enviado",
          descripcion: messageText.slice(0, 280),
          asesor_id: lead.asesor_id || null,
        }).then(bumpActivity)
      }
    } catch (err) {
      console.error("send error", err)
      showAlert("Error al enviar", "No se pudo enviar el mensaje. Intenta nuevamente.", "error")
      setMessages((prev) => { const next = prev.filter((m) => m.id !== temp.id); conversationCacheRef.current.set(lead.id, next); return next })
      fromQuickReplyRef.current = false
    } finally { setSending(false) }
  }

  const handleCopyMessage = async (m) => {
    if (!m?.mensaje) return
    try {
      await navigator.clipboard.writeText(m.mensaje)
      setCopiedMessageId(m.id)
      toast.success("Copiado")
      setTimeout(() => setCopiedMessageId(null), 1200)
    } catch {
      toast.error("No se pudo copiar")
    }
    setOpenMenuId(null)
  }

  const handleStartEdit = (m) => {
    setEditingMessageId(m.id)
    setEditingText(m.mensaje || "")
    setOpenMenuId(null)
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingText("")
  }

  const handleSaveEdit = async (m) => {
    const newText = editingText.trim()
    if (!newText || newText === m.mensaje) { handleCancelEdit(); return }
    setSavingEdit(true)
    let waEditFailed = false
    try {
      // 1. Intentar editar en WhatsApp del lead vía Evolution
      let waEditError = null
      if (m.wa_message_id && wa_jid) {
        try {
          await editWhatsAppMessage({ wa_message_id: m.wa_message_id, wa_jid, newText, instance: resolvedInstance })
        } catch (e) {
          waEditError = e?.message || String(e)
          console.warn("Evolution edit falló:", waEditError)
          console.warn("Debug payload:", { wa_message_id: m.wa_message_id, wa_jid, newText })
          waEditFailed = true
        }
      } else {
        waEditFailed = true
        waEditError = !m.wa_message_id ? "Sin wa_message_id" : "Sin wa_jid"
      }
      // 2. Update en BD
      await editLeadMessage(m.id, newText)
      // 3. Reflejar en UI
      setMessages((prev) => {
        const next = prev.map((x) => (x.id === m.id ? { ...x, mensaje: newText, edited_at: new Date().toISOString() } : x))
        conversationCacheRef.current.set(lead.id, next)
        return next
      })
      handleCancelEdit()
      if (waEditFailed) {
        const reason = waEditError && waEditError.length < 200 ? ` (${waEditError})` : ""
        toast.warning(`Editado solo en el CRM, no se modificó en WhatsApp.${reason}`)
      } else {
        toast.success("Mensaje editado")
      }
    } catch (err) {
      console.error("edit message error", err)
      showAlert("Error al editar", "No se pudo editar el mensaje. Intenta nuevamente.", "error")
    } finally {
      setSavingEdit(false)
    }
  }

  // Cerrar menú al click fuera
  useEffect(() => {
    if (!openMenuId) return
    const onClick = () => setOpenMenuId(null)
    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  }, [openMenuId])

  const handleConfirmDeleteMessage = async () => {
    if (!messageToDelete) return
    const target = messageToDelete
    setDeletingMessage(true)
    try {
      // 1. Intentar eliminar en WhatsApp del lead vía Evolution (delete-for-everyone)
      if (target.wa_message_id && wa_jid) {
        try {
          await deleteWhatsAppMessage({
            wa_message_id: target.wa_message_id,
            wa_jid,
            instance: resolvedInstance,
          })
        } catch (e) {
          console.warn("Evolution delete falló (sigue con soft-delete):", e?.message || e)
          toast.warning("No se pudo borrar en WhatsApp del lead, solo se ocultó del CRM")
        }
      }
      // 2. Soft-delete en BD
      await deleteLeadMessage(target.id)
      // 3. Reflejar en UI
      setMessages((prev) => {
        const next = prev.map((m) => (m.id === target.id ? { ...m, deleted_at: new Date().toISOString() } : m))
        conversationCacheRef.current.set(lead.id, next)
        return next
      })
      // 4. Cerrar el modal y notificar éxito
      setMessageToDelete(null)
      setDeletingMessage(false)
      toast.success("Mensaje eliminado")
    } catch (err) {
      console.error("delete message error", err)
      // Cerrar el modal de confirmación ANTES de mostrar el de error,
      // para evitar que aparezcan ambos modales encimados.
      setMessageToDelete(null)
      setDeletingMessage(false)
      showAlert("Error al eliminar", "No se pudo eliminar el mensaje. Intenta nuevamente.", "error")
    }
  }

  const handleToggleMode = async () => {
    if (!canToggleMode || toggling) return
    const newMode = !isHumanMode
    if (isHumanMode && !newMode) {
      showConfirm("¿Desactivar Modo Humano?", "El bot volverá a responder. ¿Seguro?", async () => {
        setToggling(true)
        await updateLead(lead.id, { intervencion_humana: newMode })
        setIsHumanMode(newMode)
        showAlert("Modo Bot Activado", "El bot vuelve a responder.")
        setToggling(false)
      })
      return
    }
    setToggling(true)
    await updateLead(lead.id, { intervencion_humana: newMode })
    setIsHumanMode(newMode)
    if (newMode) showAlert("Modo Humano Activado", "Ahora tú respondes.")
    setToggling(false)
  }

  const handleSaveForm = async () => {
    setSaving(true)
    try {
      const prevStage = normalizeLeadStage(lead?.estado_lead)
      const nextStage = normalizeLeadStage(formData?.estado_lead)
      await onSave({ ...lead, ...formData })
      if (prevStage !== nextStage) {
        const prevTitle = LEAD_STAGE_META[prevStage]?.title || prevStage
        const nextTitle = LEAD_STAGE_META[nextStage]?.title || nextStage
        logLeadActivity(lead.id, {
          tipo: "cambio_estado",
          titulo: `Estado: ${prevTitle} → ${nextTitle}`,
          descripcion: null,
          asesor_id: lead.asesor_id || null,
          metadata: { from: prevStage, to: nextStage },
        }).then(bumpActivity)
      }
    } catch (err) {
      toast.error("Error al guardar", { description: "No se pudieron guardar los cambios." })
    }
    setSaving(false)
  }

  // Cmd+Enter / Ctrl+Enter para guardar rápido
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault()
        if (!saving) handleSaveForm()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, saving, formData])

  // Detectar si hay cambios sin guardar
  const hasUnsavedChanges = () => {
    if (!lead) return false
    const keys = ["nombre_tutor", "telefono", "email", "nivel_educativo", "nombre_alumno", "grado_especifico", "notas", "tema_consulta"]
    return keys.some((k) => (formData[k] || "") !== (lead[k] || ""))
  }

  const handleSafeClose = () => {
    if (hasUnsavedChanges()) {
      showConfirm(
        "Cambios sin guardar",
        "Tienes cambios sin guardar. ¿Deseas salir sin guardar?",
        () => onClose(),
        "warning"
      )
    } else {
      onClose()
    }
  }

  const handleSendReminder = async () => {
    const phone = lead?.telefono || formData?.telefono
    if (!phone) { showAlert("Sin teléfono", "No hay número para enviar recordatorio.", "error"); return }
    setSendingReminder(true)
    try {
      const nombre = (formData.nombre_tutor || lead?.nombre_tutor || "estimado").trim()
      const fechaCita = formData.fecha_cita || formData.fecha_proximo_seguimiento || lead?.fecha_cita
      const fechaStr = fechaCita ? new Date(fechaCita).toLocaleString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "próxima cita"
      const msg = `Hola ${nombre} 👋 No te olvides que tu cita es el ${fechaStr}. Si necesitas reagendar, por favor avísanos. ¡Te esperamos!`
      const res = await sendWhatsAppMessage({ phone, message: msg, wa_jid: wa_jid || undefined, leadId: lead.id, instance: resolvedInstance })
      if (res?.success) {
        // Guardar en la conversación para que aparezca en el CRM
        try {
          await saveConversationMessage({
            leadId: lead.id,
            waJid: wa_jid,
            senderType: "agent",
            body: msg,
            waMessageId: res?.data?.key?.id || null,
          })
        } catch (e) {
          console.warn("[reminder] Error saving to messages:", e.message)
        }
        setReminderCount(incrementReminderCount(lead.id))
        toast.success("Recordatorio enviado por WhatsApp")
        logLeadActivity(lead.id, {
          tipo: "recordatorio",
          titulo: "Recordatorio de cita enviado",
          descripcion: msg,
          asesor_id: lead.asesor_id || null,
        }).then(bumpActivity)
      } else {
        showAlert("Error", res?.error || "No se pudo enviar el recordatorio.", "error")
      }
    } catch (err) {
      showAlert("Error", "No se pudo enviar el recordatorio. Intenta de nuevo.", "error")
    } finally { setSendingReminder(false) }
  }

  const handleMarkNotProspect = (motivo = "no_prospecto") => {
    showConfirm(
      "¿Marcar como NO prospecto?",
      "Se eliminará del CRM y se agregará a la lista de excluidos. Si esta persona vuelve a escribir al WhatsApp, NO se creará un lead nuevo.",
      async () => {
        setSaving(true)
        try {
          const { error } = await excludeWaContact({
            leadId: lead.id,
            motivo,
            excluidoPor: user?.email ?? null,
          })
          if (error) throw error
          toast.success("Contacto excluido del CRM", {
            description: "Si vuelve a escribir, ya no creará un lead.",
          })
          onClose()
        } catch (err) {
          toast.error("No se pudo excluir el contacto", { description: err?.message })
        } finally {
          setSaving(false)
        }
      },
      "destructive"
    )
  }

  const handleDeleteLead = async () => {
    setSaving(true)
    try {
      await onDelete?.(lead)
      setShowDeleteConfirm(false)
      onClose()
    } catch {
      toast.error("No se pudo eliminar el prospecto")
    } finally { setSaving(false) }
  }

  const handleSaveName = async () => {
    setEditingName(false)
    const trimmed = localName.trim()
    if (trimmed && trimmed !== (lead?.nombre_tutor || "")) {
      setFormData((p) => ({ ...p, nombre_tutor: trimmed }))
      await onSave({ ...lead, nombre_tutor: trimmed })
    } else {
      setLocalName(lead?.nombre_tutor || "")
    }
  }

  // ─────────────────────────────────────────────────────
  // ENVIAR DOCUMENTO
  // ─────────────────────────────────────────────────────
  const handleSendDocument = async (doc) => {
    setShowDocMenu(false)
    const phone = lead?.telefono || formData?.telefono
    if (!phone && !wa_jid) {
      showAlert("Sin teléfono", "No hay número para enviar el documento.", "error")
      return
    }
    setSendingDoc(true)

    const documentUrl = `${SUPABASE_URL}/storage/v1/object/public/${DOCS_BUCKET}/${encodeURIComponent(doc.fileName)}`
    const docMsg = `📄 ${doc.label}\n${documentUrl}`

    // Burbuja optimista: aparece de inmediato con spinner "Enviando documento…"
    const tempId = `tmp-doc-${Date.now()}`
    const tempMsg = {
      id: tempId,
      mensaje: docMsg,
      tipo: "agent",
      created_at: new Date().toISOString(),
      wa_jid,
      sesion_id: wa_jid,
      __sending: true,
    }
    setMessages((prev) => {
      const next = [...prev, tempMsg]
      conversationCacheRef.current.set(lead.id, next)
      return next
    })
    setTimeout(() => scrollToBottom(true), 50)

    try {
      const caption = `Información de ${doc.label} - Instituto Tepeyac`
      const res = await sendWhatsAppDocument({
        phone: phone || wa_jid?.split("@")[0] || "",
        documentUrl,
        caption,
        fileName: doc.fileName,
        wa_jid,
        instance: resolvedInstance,
      })
      if (res?.success) {
        await saveConversationMessage({
          leadId: lead.id,
          waJid: wa_jid,
          senderType: "agent",
          body: docMsg,
          waMessageId: res?.data?.key?.id || null,
        })
        // Quitar la burbuja optimista — el siguiente refetch traerá la real
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== tempId)
          conversationCacheRef.current.set(lead.id, next)
          return next
        })
        toast.success(`Documento "${doc.label}" enviado`)
        logLeadActivity(lead.id, {
          tipo: "documento_enviado",
          titulo: `Enviado: ${doc.label}`,
          descripcion: doc.fileName,
          asesor_id: lead.asesor_id || null,
          metadata: { fileName: doc.fileName, key: doc.key, url: documentUrl },
        }).then(bumpActivity)
      } else {
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== tempId)
          conversationCacheRef.current.set(lead.id, next)
          return next
        })
        showAlert("Error", res?.error || "No se pudo enviar el documento.", "error")
      }
    } catch (err) {
      setMessages((prev) => {
        const next = prev.filter((m) => m.id !== tempId)
        conversationCacheRef.current.set(lead.id, next)
        return next
      })
      showAlert("Error", "No se pudo enviar el documento. Intenta de nuevo.", "error")
    } finally {
      setSendingDoc(false)
    }
  }

  // ─────────────────────────────────────────────────────
  // SUBIR ARCHIVO DESDE LA COMPUTADORA Y ENVIAR
  // ─────────────────────────────────────────────────────
  const MAX_UPLOAD_MB = 16
  const ALLOWED_UPLOAD_TYPES = [
    "application/pdf",
    "image/png", "image/jpeg", "image/webp",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ]

  const sanitizeFileName = (name) => {
    return String(name || "archivo")
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9._-]/g, "_")
      .replace(/_+/g, "_")
      .slice(0, 120)
  }

  const handlePickFile = () => {
    if (!isHumanMode) {
      showAlert("Modo manual requerido", "Activa el Modo Manual para enviar documentos.", "warning")
      return
    }
    fileInputRef.current?.click()
  }

  const handleFileSelected = (file) => {
    if (!file) return
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      showAlert("Archivo muy grande", `El límite es ${MAX_UPLOAD_MB} MB.`, "error")
      return
    }
    if (file.type && !ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      showAlert("Tipo no permitido", "Solo PDF, imágenes (PNG/JPG/WEBP) o documentos Office.", "error")
      return
    }
    const baseName = file.name.replace(/\.[^.]+$/, "")
    setCustomCaption(baseName)
    setSendWithoutCaption(false)
    setPendingFile(file)
    setShowDocMenu(false)
  }

  const resetPendingFile = () => {
    setPendingFile(null)
    setCustomCaption("")
    setSendWithoutCaption(false)
    setUploadProgress(0)
    setUploadStage("")
    sentGuardRef.current = false
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const sentGuardRef = useRef(false)

  const handleConfirmUploadAndSend = async () => {
    if (sentGuardRef.current) return
    if (sendingDoc) return
    const file = pendingFile
    if (!file) return
    const phone = lead?.telefono || formData?.telefono
    if (!phone && !wa_jid) {
      showAlert("Sin teléfono", "No hay número para enviar el documento.", "error")
      return
    }

    sentGuardRef.current = true
    setSendingDoc(true)
    setUploadProgress(5)
    setUploadStage("Preparando archivo…")
    const toastId = toast.loading(`📤 Enviando "${file.name}"...`)

    // Burbuja optimista — aparece de inmediato con "Enviando documento…"
    const tempId = `tmp-doc-${Date.now()}`
    const optimisticName = (customCaption.trim() || file.name)
    const tempMsg = {
      id: tempId,
      mensaje: `📄 ${optimisticName}`,
      tipo: "agent",
      created_at: new Date().toISOString(),
      wa_jid,
      sesion_id: wa_jid,
      __sending: true,
    }
    setMessages((prev) => {
      const next = [...prev, tempMsg]
      conversationCacheRef.current.set(lead.id, next)
      return next
    })
    setTimeout(() => scrollToBottom(true), 50)

    try {
      const safeName = sanitizeFileName(file.name)
      const path = `custom/${lead.id}/${Date.now()}-${safeName}`

      setUploadStage("Subiendo archivo…")
      const progressTimer = setInterval(() => {
        setUploadProgress((p) => (p < 85 ? p + Math.random() * 8 : p))
      }, 250)

      const { error: upErr } = await supabase.storage
        .from(DOCS_BUCKET)
        .upload(path, file, {
          contentType: file.type || "application/octet-stream",
          upsert: false,
        })
      clearInterval(progressTimer)
      if (upErr) throw upErr
      setUploadProgress(90)
      setUploadStage("Enviando por WhatsApp…")

      const { data: pub } = supabase.storage.from(DOCS_BUCKET).getPublicUrl(path)
      const documentUrl = pub?.publicUrl
      if (!documentUrl) throw new Error("No se pudo obtener la URL pública")

      const caption = sendWithoutCaption ? "" : (customCaption.trim() || file.name)
      const ext = file.name.match(/\.[^.]+$/)?.[0] || ""
      const captionWithExt = caption && !caption.endsWith(ext) ? `${caption}${ext}` : caption
      const isImage = /^image\//i.test(file.type) || /\.(png|jpe?g|webp|gif)$/i.test(file.name)

      const res = isImage
        ? await sendWhatsAppImage({
            phone: phone || wa_jid?.split("@")[0] || "",
            imageUrl: documentUrl,
            caption,
            wa_jid,
            instance: resolvedInstance,
          })
        : await sendWhatsAppDocument({
            phone: phone || wa_jid?.split("@")[0] || "",
            documentUrl,
            caption,
            fileName: captionWithExt || file.name,
            wa_jid,
            instance: resolvedInstance,
          })

      if (res?.success) {
        setUploadProgress(100)
        setUploadStage(isImage ? "✅ ¡Imagen enviada!" : "✅ ¡Documento enviado!")
        const displayName = caption || file.name
        const docMsg = isImage
          ? `${documentUrl}${caption ? `\n${caption}` : ""}`
          : `📄 ${displayName}\n${documentUrl}`
        await saveConversationMessage({
          leadId: lead.id,
          waJid: wa_jid,
          senderType: "agent",
          body: docMsg,
          waMessageId: res?.data?.key?.id || null,
        })
        // Quitar burbuja optimista — el refetch traerá la real
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== tempId)
          conversationCacheRef.current.set(lead.id, next)
          return next
        })
        toast.success(
          isImage ? `🖼️ Imagen entregada al prospecto` : `📄 "${displayName}" entregado al prospecto`,
          { id: toastId, duration: 3000 }
        )
        logLeadActivity(lead.id, {
          tipo: "documento_enviado",
          titulo: `Enviado: ${displayName}`,
          descripcion: "Archivo subido manualmente",
          asesor_id: lead.asesor_id || null,
          metadata: { fileName: file.name, caption, path, custom: true, url: documentUrl, isImage },
        }).then(bumpActivity)
        setTimeout(() => resetPendingFile(), 1200)
      } else {
        sentGuardRef.current = false
        setMessages((prev) => {
          const next = prev.filter((m) => m.id !== tempId)
          conversationCacheRef.current.set(lead.id, next)
          return next
        })
        toast.error(`No se pudo enviar: ${res?.error || "error desconocido"}`, { id: toastId, duration: 4000 })
        showAlert("Error", res?.error || "No se pudo enviar el documento.", "error")
        setUploadProgress(0)
        setUploadStage("")
      }
    } catch (err) {
      sentGuardRef.current = false
      setMessages((prev) => {
        const next = prev.filter((m) => m.id !== tempId)
        conversationCacheRef.current.set(lead.id, next)
        return next
      })
      console.error("[upload-send]", err)
      toast.error(`Error al subir: ${err?.message || "desconocido"}`, { id: toastId, duration: 4000 })
      showAlert("Error", err?.message || "No se pudo subir el documento.", "error")
      setUploadProgress(0)
      setUploadStage("")
    } finally {
      // Safeguard: si por alguna razón el toast loading no fue reemplazado, lo dismiss
      setTimeout(() => toast.dismiss(toastId), 4500)
      setSendingDoc(false)
    }
  }

  // ─────────────────────────────────────────────────────
  // RENDER CONTENIDO DE MENSAJE (media detection)
  // ─────────────────────────────────────────────────────
  const getFileNameFromUrl = (url) => {
    try {
      const clean = url.split("?")[0].split("#")[0]
      const last = clean.substring(clean.lastIndexOf("/") + 1)
      return decodeURIComponent(last) || "Documento"
    } catch {
      return "Documento"
    }
  }

  const renderMessageContent = (mensajeOrObj) => {
    // Acepta string (legado) u objeto mensaje completo (para detectar __sending)
    const m = typeof mensajeOrObj === "object" && mensajeOrObj !== null ? mensajeOrObj : null
    const mensaje = m ? m.mensaje : mensajeOrObj
    const isSending = Boolean(m?.__sending)
    if (!mensaje) return null
    const text = String(mensaje)

    // Audio tag — extrae la transcripción si viene con <audio>...</audio>
    if (text.includes("<audio>") || text.includes("[audio]")) {
      const match = text.match(/<audio>\s*([\s\S]*?)\s*<\/audio>/i)
      const transcript = match ? match[1].trim() : null
      // El body puede contener texto extra ANTES o DESPUÉS del bloque <audio>...</audio>
      const extra = text.replace(/<audio>[\s\S]*?<\/audio>/i, "").replace(/\[audio\]/gi, "").trim()
      return (
        <div className="space-y-1.5">
          {extra && (
            <div className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed">
              {extra}
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100/60 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-700/50 w-fit">
            <span>🎵</span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Mensaje de voz
            </span>
          </div>
          {transcript && (
            <div className="text-[12.5px] italic text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words pl-1 border-l-2 border-slate-300 dark:border-slate-600 ml-1 pl-2">
              "{transcript}"
            </div>
          )}
        </div>
      )
    }

    // Image URL detection (prioridad sobre documento)
    const imgMatch = text.match(/(https?:\/\/\S+?\.(?:png|jpg|jpeg|gif|webp)(?:\?\S*)?)/i)
    if (imgMatch) {
      const remaining = text.replace(imgMatch[0], "").trim()
      return (
        <div>
          <img src={imgMatch[0]} alt="Imagen" className="max-w-full rounded-lg max-h-48 object-cover mb-1" loading="lazy" />
          {remaining && <div className="whitespace-pre-wrap break-words text-[12.5px] leading-relaxed">{remaining}</div>}
        </div>
      )
    }

    // Document URL detection (PDF, Office) — acepta query strings (?token=...)
    const docMatch = text.match(/(https?:\/\/\S+?\.(?:pdf|docx?|xlsx?|pptx?)(?:\?\S*)?)/i)
    if (docMatch) {
      const url = docMatch[0]
      const fileName = getFileNameFromUrl(url)
      const remaining = text.replace(url, "").trim()
      const ext = (fileName.match(/\.([a-zA-Z0-9]+)(?:\?|$)/)?.[1] || "").toLowerCase()
      const isPdf = ext === "pdf"
      // Paleta: solo el ÍCONO y el subtítulo llevan color · el contenedor es neutro para contrastar bien sobre la burbuja del mensaje
      const accentIcon = isPdf ? "text-rose-600 dark:text-rose-300" : "text-blue-600 dark:text-blue-300"
      const accentBg = isPdf ? "bg-rose-100 dark:bg-rose-900/50" : "bg-blue-100 dark:bg-blue-900/50"
      const accentSub = isPdf ? "text-rose-700 dark:text-rose-300" : "text-blue-700 dark:text-blue-300"
      const accentSpin = isPdf ? "border-rose-500" : "border-blue-500"
      const Wrapper = isSending ? "div" : "a"
      const baseClasses = "flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white/95 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700"
      const wrapperProps = isSending
        ? { className: `${baseClasses} cursor-wait` }
        : { href: url, target: "_blank", rel: "noopener noreferrer", className: `${baseClasses} hover:bg-white dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors` }
      return (
        <div>
          <Wrapper {...wrapperProps}>
            <div className={`flex items-center justify-center h-9 w-9 rounded-lg ${accentBg} flex-shrink-0`}>
              {isSending ? (
                <div className={`h-4 w-4 rounded-full border-2 ${accentSpin} border-t-transparent animate-spin`} />
              ) : (
                <FileText className={`h-5 w-5 ${accentIcon}`} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold truncate text-slate-900 dark:text-slate-100">{fileName}</div>
              <div className={`text-[10.5px] ${accentSub} flex items-center gap-1 font-medium`}>
                {isSending ? (
                  <>
                    <span className="inline-block h-1 w-1 rounded-full bg-current animate-pulse" />
                    Enviando documento…
                  </>
                ) : (
                  <>
                    <span className="uppercase font-bold tracking-wide">{ext || "DOC"}</span>
                    <span className="opacity-50">·</span>
                    <span>Abrir</span>
                  </>
                )}
              </div>
            </div>
            {!isSending && <Download className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />}
          </Wrapper>
          {remaining && <div className="mt-1 whitespace-pre-wrap break-words text-[12.5px] leading-relaxed">{remaining}</div>}
        </div>
      )
    }

    // Helper para colores acentos del documento
    const docAccent = (isPdf) => ({
      iconBg: isPdf ? "bg-rose-100 dark:bg-rose-900/50" : "bg-blue-100 dark:bg-blue-900/50",
      icon: isPdf ? "text-rose-600 dark:text-rose-300" : "text-blue-600 dark:text-blue-300",
      sub: isPdf ? "text-rose-700 dark:text-rose-300" : "text-blue-700 dark:text-blue-300",
      spin: isPdf ? "border-rose-500" : "border-blue-500",
    })

    // Placeholder de documento sin URL (n8n / Evolution a veces manda solo esto)
    if (/^\s*(\[document\]|<document>|\[pdf\]|\[archivo\])/i.test(text)) {
      const clean = text
        .replace(/^\s*(\[document\]|<document>|\[pdf\]|\[archivo\])\s*:?\s*/i, "")
        .trim() || "Documento"
      const isPdf = /\.pdf(\?|$)/i.test(clean) || /\bpdf\b/i.test(clean)
      const a = docAccent(isPdf)
      return (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white/95 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700">
          <div className={`flex items-center justify-center h-9 w-9 rounded-lg ${a.iconBg} flex-shrink-0`}>
            {isSending
              ? <div className={`h-4 w-4 rounded-full border-2 ${a.spin} border-t-transparent animate-spin`} />
              : <FileText className={`h-5 w-5 ${a.icon}`} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-semibold truncate text-slate-900 dark:text-slate-100">{clean}</div>
            <div className={`text-[10.5px] ${a.sub} font-medium`}>
              {isSending ? "Enviando documento…" : "Documento"}
            </div>
          </div>
        </div>
      )
    }

    // Prefijo "📄 nombre" o "Documento enviado: nombre" (sin URL aún)
    if (text.startsWith("Documento enviado:") || text.startsWith("📄 ")) {
      const clean = text.replace(/^📄\s*/, "").replace(/^Documento enviado:\s*/, "").trim() || "Documento"
      const isPdf = /\.pdf(\?|$)/i.test(clean) || !/\.[a-z]{3,4}$/i.test(clean)
      const a = docAccent(isPdf)
      return (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white/95 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700">
          <div className={`flex items-center justify-center h-9 w-9 rounded-lg ${a.iconBg} flex-shrink-0`}>
            {isSending
              ? <div className={`h-4 w-4 rounded-full border-2 ${a.spin} border-t-transparent animate-spin`} />
              : <FileText className={`h-5 w-5 ${a.icon}`} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-semibold truncate text-slate-900 dark:text-slate-100">{clean}</div>
            <div className={`text-[10.5px] ${a.sub} flex items-center gap-1 font-medium`}>
              {isSending ? (
                <>
                  <span className="inline-block h-1 w-1 rounded-full bg-current animate-pulse" />
                  Enviando documento…
                </>
              ) : (
                <>
                  <span className="uppercase font-bold tracking-wide">{isPdf ? "PDF" : "DOC"}</span>
                  <span className="opacity-50">·</span>
                  <span>Documento enviado</span>
                </>
              )}
            </div>
          </div>
        </div>
      )
    }

    // Image tag from bot
    if (text.includes("<image>") || text.includes("[image]")) {
      return (
        <div className="flex items-center gap-2 text-[11px] italic text-slate-500">
          <span>🖼️</span>
          <span>Imagen</span>
        </div>
      )
    }

    // Default: plain text
    return <div className="whitespace-pre-wrap break-words text-[12.5px] sm:text-[13px] leading-relaxed">{text}</div>
  }

  const handleQuickReply = (replyText, label) => {
    setText(replyText)
    setShowQuickReplies(false)
    // Marcar que el próximo envío viene de una respuesta rápida,
    // para evitar duplicar en el timeline.
    fromQuickReplyRef.current = true
    if (label && lead?.id) {
      logLeadActivity(lead.id, {
        tipo: "respuesta_rapida",
        titulo: `Respuesta rápida: ${label}`,
        descripcion: replyText.slice(0, 280),
        asesor_id: lead.asesor_id || null,
      }).then(bumpActivity)
    }
  }

  // ─────────────────────────────────────────────────────
  // EXPORTAR CHAT
  // ─────────────────────────────────────────────────────
  const handleExportChat = () => {
    if (!messages.length) return

    const lines = messages.map(m => {
      const date = m.created_at ? new Date(m.created_at).toLocaleString('es-MX') : 'Sin fecha'
      const actor = m.tipo === 'agent' ? 'Asesor/Agente' :
        m.tipo === 'bot' ? 'Bot' :
          `Cliente (${lead.nombre_tutor || 'Prospecto'})`
      return `[${date}] ${actor}: ${m.mensaje}`
    })

    const content = lines.join('\n')
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `chat_${lead.nombre_tutor || 'lead'}_${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const stage = normalizeLeadStage(lead?.estado_lead)
  const stageMeta = LEAD_STAGE_META[stage] || {}
  const StageIcon = stageMeta.icon || User
  const displayName = formData.nombre_tutor || lead?.nombre_tutor || "Sin nombre"
  const initials = displayName.split(" ").map((p) => p.charAt(0)).join("").slice(0, 2).toUpperCase() || "LD"

  const levelKey = normalizeLevel(formData.nivel_educativo || lead?.nivel_educativo)
  const levelMeta = LEVEL_META[levelKey] || { label: "Sin nivel", icon: GraduationCap }
  const LevelIcon = levelMeta.icon

  if (!open || !lead) return null

  const inputCls = "h-8 text-xs px-2.5 rounded-lg border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-950/50 focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300 dark:focus:border-rose-700 transition-all placeholder:text-slate-400/60"
  const selectCls = "h-8 w-full rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-slate-50/50 dark:bg-slate-950/50 px-2 text-xs focus:ring-2 focus:ring-rose-500/15 focus:border-rose-300 transition-all"
  const labelCls = "text-[10px] uppercase tracking-wider font-semibold text-slate-500/80 dark:text-slate-400/80 mb-1 flex items-center gap-1.5"
  const sectionCls = "rounded-xl border border-slate-100 dark:border-slate-800/60 bg-white dark:bg-slate-900/50 p-3 space-y-2.5 shadow-sm shadow-slate-100/50 dark:shadow-none"

  const infoPanel = (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
      {/* Header tinto corporativo */}
      <div className="flex-shrink-0 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #7a1b33 0%, #5a1523 60%, #420f1a 100%)" }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="relative px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-white font-bold flex-shrink-0 text-sm ring-2 ring-white/20 shadow-lg">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h2 className="text-sm font-bold text-white truncate">{displayName}</h2>
                {lead.source && (
                  <LeadSourceBadge source={lead.source} sourceDetail={lead.source_detail} size={22} />
                )}
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold text-white/90 ring-1 ring-white/15" style={{ backgroundColor: `${stageMeta.accent || "#6366f1"}80` }}>
                  <StageIcon className="h-3 w-3" />
                  {stageMeta.title || lead.estado_lead || "Sin estado"}
                </span>
                {lead.source && (
                  <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold text-white/80 bg-white/10 ring-1 ring-white/15">
                    {getSourceTooltip(lead.source, lead.source_detail, lead.source_metadata)}
                  </span>
                )}
                {taskStats.pending > 0 && (
                  <span
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold ring-1 ${
                      taskStats.overdue > 0
                        ? "bg-red-500 text-white ring-red-300/60 shadow-sm shadow-red-900/30"
                        : "bg-amber-400 text-amber-950 ring-amber-200/60"
                    }`}
                    title={taskStats.overdue > 0 ? `${taskStats.overdue} vencida(s)` : "Tareas pendientes"}
                  >
                    <AlertCircle className="h-3 w-3" />
                    {taskStats.overdue > 0
                      ? `${taskStats.overdue} vencida${taskStats.overdue !== 1 ? "s" : ""} · ${taskStats.pending} pend.`
                      : `${taskStats.pending} tarea${taskStats.pending !== 1 ? "s" : ""} pend.`}
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all" title="Ocultar panel">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSaveForm() }} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3 custom-scrollbar">

        {/* Procedencia — banner destacado de dónde vino el prospecto */}
        {lead.source && (() => {
          const sourceMeta = getSourceMeta(lead.source)
          const detailLabel = getSourceDetailLabel(lead.source_detail, lead.source_metadata)
          const adUrl = lead.source_metadata?.ad_source_url
          return (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800/60 p-3 flex items-center gap-3 shadow-sm">
              <LeadSourceBadge source={lead.source} sourceDetail={lead.source_detail} size={36} ringed={false} />
              <div className="flex-1 min-w-0">
                <p className="text-[9px] uppercase tracking-widest font-bold text-slate-600 dark:text-slate-300">
                  Procedencia
                </p>
                <p className="text-[13px] font-bold text-slate-900 dark:text-slate-50 truncate leading-tight">
                  {sourceMeta.label}
                </p>
                {detailLabel && (
                  adUrl ? (
                    <a
                      href={adUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Ver el anuncio que tocó este prospecto"
                      className="text-[10.5px] text-slate-600 dark:text-slate-300 truncate font-medium hover:text-[#0866FF] dark:hover:text-blue-300 hover:underline underline-offset-2 transition-colors block"
                    >
                      {detailLabel} ↗
                    </a>
                  ) : (
                    <p className="text-[10.5px] text-slate-600 dark:text-slate-300 truncate font-medium">
                      {detailLabel}
                    </p>
                  )
                )}
              </div>
            </div>
          )
        })()}

        {/* Contacto */}
        <div className={sectionCls}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
            <Phone className="h-3 w-3" /> Contacto
          </p>
          <div>
            <Label className={labelCls}><User className="h-3 w-3" /> Nombre del tutor</Label>
            <Input value={formData.nombre_tutor || ""} onChange={(e) => setFormData((p) => ({ ...p, nombre_tutor: e.target.value }))} className={inputCls} placeholder="Nombre completo" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className={labelCls}><Phone className="h-3 w-3" /> Teléfono</Label>
              <Input value={formData.telefono || ""} onChange={(e) => setFormData((p) => ({ ...p, telefono: e.target.value }))} className={inputCls} placeholder="Teléfono" />
            </div>
            <div>
              <Label className={labelCls}><Mail className="h-3 w-3" /> Email</Label>
              <Input type="email" value={formData.email || ""} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} className={inputCls} placeholder="Email" />
            </div>
          </div>
        </div>

        {/* Asignación (campus + asesor).
            El nivel/grado del alumno vive ahora SOLO en "Hijos registrados"
            — el trigger de la DB propaga el primer hijo al lead automáticamente. */}
        <div className={sectionCls}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
            <GraduationCap className="h-3 w-3" /> Asignación
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className={labelCls}><MapPin className="h-3 w-3" /> Campus</Label>
              <div
                className="h-8 px-2.5 rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-slate-100/60 dark:bg-slate-800/60 text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5 cursor-not-allowed select-none"
                title="Campus fijo por default — no editable"
              >
                <span className="truncate">{formData.campus || "IT Innovation School Guadalajara"}</span>
                <span className="ml-auto text-[8px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-bold">Fijo</span>
              </div>
            </div>
            <div>
              <Label className={labelCls}><UserCircle className="h-3 w-3" /> Asesor</Label>
              <select
                value={formData.asesor_id || ""}
                onChange={async (e) => {
                  const newAsesorId = e.target.value || null
                  const currentAsesorId = formData.asesor_id || null
                  const selected = asesoresList.find(a => String(a.id) === e.target.value)
                  const isAdmin = user?.isAdmin || user?.rol === "admin"
                  const isReassign = currentAsesorId && newAsesorId && currentAsesorId !== newAsesorId
                  const targetIsAdmin = selected?.rol === "admin"

                  // Si es CAP cambiando a otra CAP → flujo de transferencia con confirmación
                  if (isReassign && !isAdmin && !targetIsAdmin) {
                    setTransferPreselected(newAsesorId)
                    setShowTransferModal(true)
                    e.target.value = currentAsesorId  // revertir UI
                    return
                  }

                  // Asignación inicial o admin → cambio directo
                  const updates = selected
                    ? { asesor_id: selected.id, asesor_nombre: selected.nombre, asesor_email: selected.email }
                    : { asesor_id: null, asesor_nombre: "", asesor_email: "" }
                  setFormData((p) => ({ ...p, ...updates }))
                  try {
                    await updateLead(lead.id, { asesor_id: updates.asesor_id })
                    toast.success("Prospecto actualizado")
                  } catch {
                    toast.error("Error al cambiar asesor")
                  }
                }}
                className={selectCls}
                style={{ borderColor: "#7a1b33", fontWeight: 600 }}
                title={user?.isAdmin ? "Cambio directo (admin)" : "Cambiar a otra CAP requiere confirmación de la receptora"}
              >
                <option value="">Sin asignar</option>
                {asesoresList.map((a) => (
                  <option key={a.id} value={a.id}>{a.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tema de consulta (único campo de este bloque — estado/municipio ya no son relevantes) */}
        <div className={sectionCls}>
          <Label className={labelCls}><AlertCircle className="h-3 w-3" /> Tema de consulta</Label>
          <Input value={formData.tema_consulta || ""} onChange={(e) => setFormData((p) => ({ ...p, tema_consulta: e.target.value }))} className={inputCls} placeholder="¿Qué consulta tiene?" />
        </div>

        {/* Hijos registrados (familia) */}
        <div className={sectionCls}>
          <LeadChildrenSection
            leadId={lead.id}
            onChange={(rows) => {
              setFormData((p) => ({
                ...p,
                num_hijos: Math.max(rows.length, 1),
                es_familia: rows.length >= 2,
              }))
            }}
          />
        </div>

        {/* Tareas pendientes del prospecto */}
        <div className={sectionCls}>
          <LeadTasksSection
            leadId={lead.id}
            asesorId={lead.asesor_id || null}
            reloadKey={tasksReload}
            onActivityChange={bumpActivity}
            onStatsChange={setTaskStats}
          />
        </div>

        {/* Timeline de actividad */}
        <div className={sectionCls}>
          <LeadActivityTimeline
            leadId={lead.id}
            asesorId={lead.asesor_id || null}
            reloadKey={activityReload}
          />
        </div>

        {/* Agendar Cita - Diseño tinto destacado */}
        <div className="rounded-xl border border-rose-200/70 dark:border-rose-800/30 bg-gradient-to-br from-rose-50/80 to-pink-50/30 dark:from-rose-950/15 dark:to-pink-950/10 p-3 space-y-2.5 shadow-sm shadow-rose-100/40 dark:shadow-none relative overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-rose-200/20 to-transparent rounded-bl-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(122,27,51,0.1)" }}>
                <Calendar className="h-3.5 w-3.5" style={{ color: "#7a1b33" }} />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#7a1b33" }}>Agendar Cita</span>
            </div>
            {formData.fecha_cita && (
              <span className="text-[10px] font-semibold text-white px-2 py-0.5 rounded-md" style={{ backgroundColor: "#7a1b33" }}>
                {new Date(formData.fecha_cita).toLocaleDateString("es-MX", { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>

          <div className="relative flex gap-2">
            <div className="flex-1">
              <DateTimePicker
                value={formData.fecha_cita || formData.fecha_proximo_seguimiento || null}
                onChange={(iso) => setFormData((p) => ({ ...p, fecha_cita: iso }))}
                onConfirm={(iso) => {
                  setFormData((p) => ({ ...p, fecha_cita: iso, cita_confirmada: true }))
                  onSave({ ...lead, ...formData, fecha_cita: iso, cita_confirmada: true })
                  const fechaLegible = new Date(iso).toLocaleString("es-MX", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })
                  toast.success("Cita agendada y guardada", { description: fechaLegible })
                  logLeadActivity(lead.id, {
                    tipo: "cita_agendada",
                    titulo: "Cita agendada",
                    descripcion: fechaLegible,
                    asesor_id: lead.asesor_id || null,
                  }).then(bumpActivity)
                  // Crear automáticamente una tarea tipo cita ligada al lead
                  createTask({
                    leadId: lead.id,
                    asesorId: lead.asesor_id || null,
                    titulo: `Cita con ${lead.nombre_tutor || "prospecto"}`,
                    tipo: "cita",
                    prioridad: "alta",
                    fechaVencimiento: iso,
                  }).then(({ error }) => {
                    if (!error) bumpTasks()
                  })
                }}
                placeholder="Seleccionar fecha y hora"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`h-9 w-9 rounded-xl transition-all ${reminderCount > 0 ? "bg-rose-100/60 shadow-sm" : "hover:bg-rose-100/40"}`}
              style={{ color: "#7a1b33" }}
              onClick={handleSendReminder}
              disabled={sendingReminder || !(formData.telefono || lead?.telefono)}
              title="Enviar recordatorio por WhatsApp"
            >
              <Bell className="h-4 w-4" />
            </Button>
          </div>
          {reminderCount > 0 && <p className="text-[10px] text-right font-medium" style={{ color: "#7a1b33aa" }}>Recordatorios enviados: {reminderCount}</p>}
        </div>

        {/* Notas */}
        <div className={sectionCls}>
          <Label className={labelCls}><AlertCircle className="h-3 w-3" /> Notas</Label>
          <Textarea value={formData.notas || ""} onChange={(e) => setFormData((p) => ({ ...p, notas: e.target.value }))} placeholder="Escribe una nota..." className="min-h-[70px] text-xs leading-relaxed bg-slate-50/50 dark:bg-slate-950/50 resize-none rounded-lg p-2.5 border-slate-200/80 dark:border-slate-700/80 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all" />
        </div>

        {/* Acciones */}
        <div className="pt-1 pb-2 flex flex-col gap-2">
          <Button type="submit" className="w-full h-10 text-xs font-bold rounded-xl shadow-lg text-white transition-all hover:shadow-xl" style={{ backgroundColor: "#7a1b33", boxShadow: "0 8px 24px rgba(122,27,51,0.25)" }} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-2" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
          <div className="grid grid-cols-3 gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] rounded-lg border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all" onClick={() => onRequestArchive?.(lead)}>
              <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivar
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] rounded-lg border-red-200/80 dark:border-red-900/50 text-red-500/80 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 hover:border-red-300 transition-all" onClick={() => handleMarkNotProspect("no_prospecto")} title="Excluir del CRM. Si vuelve a escribir, ya no creará lead.">
              <Ban className="h-3.5 w-3.5 mr-1.5" /> No prospecto
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-8 text-[11px] rounded-lg border-red-300 dark:border-red-800 text-red-600 hover:text-white hover:bg-red-600 dark:hover:bg-red-700 transition-all" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar
            </Button>
          </div>
        </div>
      </form>
    </div>
  )


  const chatPanel = (
    <div className="flex flex-col flex-1 min-w-0 min-h-0">
      <div className="flex-shrink-0 flex items-center gap-3 px-3 sm:px-4 py-2.5" style={{ background: waHeaderBg }}>
        {!isSidebarOpen && (
          <button onClick={() => setIsSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors flex-shrink-0 sm:block hidden" title="Mostrar panel de info">
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}
        {/* Profile picture */}
        {waProfilePic && !avatarError ? (
          <img src={waProfilePic} alt="" onError={() => setAvatarError(true)} className="h-9 w-9 rounded-full object-cover ring-2 ring-white/30 flex-shrink-0 shadow-md" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {(lead?.nombre_tutor || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {editingName ? (
            <input
              type="text"
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveName(); if (e.key === "Escape") { setEditingName(false); setLocalName(lead?.nombre_tutor || "") } }}
              autoFocus
              className="w-full bg-white/20 text-white font-semibold text-sm px-2 py-1 rounded border border-white/40 focus:outline-none focus:ring-1 focus:ring-white/50"
              placeholder="Nombre del prospecto"
            />
          ) : (
            <button type="button" onClick={() => { setLocalName(formData.nombre_tutor || lead?.nombre_tutor || ""); setEditingName(true) }} className="w-full text-left flex items-center gap-1.5 group">
              <span className="font-bold text-white text-sm truncate">{(formData.nombre_tutor || lead?.nombre_tutor || "Prospecto").trim() || "Sin nombre"}</span>
              <Pencil className="h-3 w-3 text-white/80 flex-shrink-0" />
            </button>
          )}
          <span className="text-white/70 text-[11px] block">{messages.length} mensajes</span>
        </div>
        <button
          onClick={() => setShowTransferModal(true)}
          title="Transferir prospecto a otra asesora"
          className="p-2 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors flex-shrink-0 hidden sm:block"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
        <button
          onClick={handleExportChat}
          title="Exportar Chat a TXT"
          className="p-2 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors flex-shrink-0 hidden sm:block"
        >
          <Download className="h-4 w-4" />
        </button>
        <button onClick={handleSafeClose} className="hidden sm:flex p-2 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors flex-shrink-0" title="Cerrar">
          <X className="h-4 w-4" />
        </button>
      </div>

      {canToggleMode && (
        <div className={`flex-shrink-0 flex items-center justify-between gap-2 px-3.5 py-2 border-b transition-colors duration-300 ${isHumanMode ? "bg-gradient-to-r from-emerald-50 to-teal-50/50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-200/60 dark:border-emerald-800/40" : "bg-gradient-to-r from-rose-50/50 to-amber-50/30 dark:from-rose-950/20 dark:to-amber-950/10 border-rose-200/40 dark:border-rose-800/30"}`}>
          <div className="flex items-center gap-2">
            <div className={`h-6 w-6 rounded-lg flex items-center justify-center transition-colors duration-300 ${isHumanMode ? "bg-emerald-500/15" : "bg-rose-500/10"}`}>
              {isHumanMode ? <User className="h-3.5 w-3.5 text-emerald-600" /> : <Bot className="h-3.5 w-3.5" style={{ color: "#7a1b33" }} />}
            </div>
            <div>
              <span className={`text-xs font-bold transition-colors duration-300 ${isHumanMode ? "text-emerald-700 dark:text-emerald-300" : "dark:text-rose-300"}`} style={!isHumanMode ? { color: "#7a1b33" } : {}}>
                {isHumanMode ? "Modo Manual" : "IA Automática"}
              </span>
              {!isHumanMode && <span className="text-[9px] text-slate-400 dark:text-slate-500 block leading-tight">Activa Manual para escribir</span>}
            </div>
          </div>
          <button
            onClick={handleToggleMode}
            disabled={toggling}
            className={`h-6 w-11 rounded-full flex items-center transition-all duration-300 flex-shrink-0 shadow-inner ${isHumanMode ? "bg-emerald-500 shadow-emerald-600/20" : "bg-slate-300 dark:bg-slate-600 shadow-slate-400/20"}`}
          >
            <span className={`h-5 w-5 bg-white rounded-full shadow-md transform transition-transform duration-300 flex-shrink-0 ${isHumanMode ? "translate-x-[22px]" : "translate-x-[2px]"}`} />
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain p-4 space-y-3 relative touch-pan-y chat-area-bg"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0" aria-hidden>
          <img src="/it-logo.webp" alt="" className="w-32 h-auto max-w-[45%] max-h-[25%] object-contain opacity-[0.06] dark:opacity-[0.05]" />
        </div>
        {loading ? (
          <div className="relative z-10 flex items-center justify-center h-40">
            <div className="animate-spin h-10 w-10 rounded-full border-2 border-emerald-300 border-t-emerald-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="relative z-10 flex flex-col items-center justify-center h-40 text-center">
            <MessageSquare className="h-12 w-12 text-slate-400" style={{ color: waHeaderBg }} />
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 font-medium">Sin mensajes aún</p>
          </div>
        ) : (
          <div className="relative z-10 space-y-2.5 pb-2">
            {messages.map((m, idx) => {
              const tipo = m.tipo || "cliente"
              const colors = BUBBLE_COLORS[tipo] || BUBBLE_COLORS.cliente
              const right = tipo === "agent" || tipo === "bot"
              const prev = idx > 0 ? messages[idx - 1] : null
              const showDaySeparator = !prev || getDayKey(prev.created_at) !== getDayKey(m.created_at)
              return (
                <Fragment key={m.id || idx}>
                  {showDaySeparator && (
                    <div className="flex justify-center my-3 first:mt-0">
                      <span className="px-3 py-1 rounded-full bg-slate-200/70 dark:bg-slate-700/70 backdrop-blur-sm text-[10px] font-semibold text-slate-600 dark:text-slate-300 shadow-sm uppercase tracking-wide">
                        {formatDayLabel(m.created_at)}
                      </span>
                    </div>
                  )}
                  <div className={`flex ${right ? "justify-end" : "justify-start"} gap-1.5`}>
                    {!right && (
                      <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ring-2 ring-white/80 dark:ring-slate-800/80 shadow-sm" style={{ backgroundColor: colors.avatarBg }}>
                        <span className="text-[10px] font-bold" style={{ color: colors.avatarText }}>{(formData.nombre_tutor || lead?.nombre_tutor || "?")[0].toUpperCase()}</span>
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] sm:max-w-[72%] rounded-2xl px-3 py-2 sm:px-3.5 sm:py-2.5 ${right ? "rounded-br-md" : "rounded-bl-md"} group relative`}
                      style={{ backgroundColor: colors.bg, color: colors.text, boxShadow: colors.shadow }}
                    >
                      {colors.label && <span className="block text-[9px] font-bold mb-0.5 tracking-wide uppercase opacity-80" style={{ color: colors.labelColor }}>{colors.label}</span>}
                      {m.deleted_at ? (
                        <span className="italic text-slate-400 text-[12px] flex items-center gap-1.5">
                          <Ban className="h-3 w-3" /> Mensaje eliminado
                        </span>
                      ) : editingMessageId === m.id ? (
                        <div className="flex flex-col gap-2 min-w-[220px]">
                          <Textarea
                            autoFocus
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Escape") { e.preventDefault(); handleCancelEdit() }
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSaveEdit(m) }
                            }}
                            className="min-h-[60px] text-[13px] leading-relaxed bg-white/90 dark:bg-slate-800/90 border-slate-300 dark:border-slate-600 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 text-slate-900 dark:text-slate-100 rounded-lg p-2"
                          />
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              disabled={savingEdit}
                              onClick={handleCancelEdit}
                              className="px-2.5 py-1 rounded-md text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-700/60 transition-colors disabled:opacity-50"
                            >
                              Cancelar
                            </button>
                            <button
                              type="button"
                              disabled={savingEdit || !editingText.trim() || editingText.trim() === m.mensaje}
                              onClick={() => handleSaveEdit(m)}
                              className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                              {savingEdit && <div className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                              Guardar
                            </button>
                          </div>
                        </div>
                      ) : (
                        renderMessageContent(m)
                      )}
                      <div className="text-[9px] mt-1.5 flex justify-end gap-1 items-center" style={{ color: colors.timeTxt }}>
                        {m.edited_at && !m.deleted_at && editingMessageId !== m.id && (
                          <span className="italic opacity-80 mr-1">editado</span>
                        )}
                        {new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        {right && tipo === "agent" && <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" />}
                      </div>

                      {/* Botón kebab (solo mensajes propios, no eliminados, no en edición, no de memoria AI) */}
                      {right && !m.deleted_at && editingMessageId !== m.id && !String(m.id || "").startsWith("n8n-") && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === m.id ? null : m.id) }}
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center"
                          title="Más opciones"
                        >
                          <MoreVertical className="h-3 w-3" />
                        </button>
                      )}

                      {/* Dropdown del kebab */}
                      {openMenuId === m.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="absolute z-30 top-4 right-0 min-w-[170px] rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden animate-in zoom-in-95 fade-in-50 duration-150"
                        >
                          {right && !m.deleted_at && (
                            <button
                              type="button"
                              onClick={() => handleStartEdit(m)}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5 text-emerald-600" /> Editar
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleCopyMessage(m)}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors border-t border-slate-100 dark:border-slate-800"
                          >
                            {copiedMessageId === m.id ? <Check className="h-3.5 w-3.5 text-blue-600" /> : <Copy className="h-3.5 w-3.5 text-blue-600" />}
                            {copiedMessageId === m.id ? "Copiado" : "Copiar texto"}
                          </button>
                          {right && !m.deleted_at && (
                            <button
                              type="button"
                              onClick={() => { setMessageToDelete(m); setOpenMenuId(null) }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors border-t border-slate-100 dark:border-slate-800"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Eliminar
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {right && (
                      <div className="h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ring-2 ring-white/80 dark:ring-slate-800/80 shadow-sm" style={{ backgroundColor: colors.avatarBg }}>
                        {tipo === "agent" ? <User className="h-3 w-3" style={{ color: "#7a1b33" }} /> : <Bot className="h-3 w-3 text-green-700" />}
                      </div>
                    )}
                  </div>
                </Fragment>
              )
            })}
          </div>
        )}
      </div>

      <div className="flex-shrink-0 bg-white/95 dark:bg-slate-800/95 backdrop-blur-lg border-t border-slate-200/80 dark:border-slate-700/80">
        {/* Menú de documentos */}
        {showDocMenu && (
          <div className="px-3 pt-2.5 sm:px-4 animate-in slide-in-from-bottom-2 duration-150">
            <div className="p-2.5 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="h-3 w-3 text-red-500" /> Documentos del colegio
                </span>
                <button onClick={() => setShowDocMenu(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                {DOCUMENT_CATALOG.map((doc) => {
                  const docUrl = `${SUPABASE_URL}/storage/v1/object/public/${DOCS_BUCKET}/${encodeURIComponent(doc.fileName)}`
                  return (
                    <div
                      key={doc.key}
                      className="flex items-center gap-1 rounded-lg text-[11px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 hover:border-red-300 dark:hover:border-red-800 transition-all overflow-hidden group"
                    >
                      <button
                        onClick={() => handleSendDocument(doc)}
                        disabled={sendingDoc || !isHumanMode}
                        className="flex items-center gap-2 px-2.5 py-2 flex-1 min-w-0 text-left hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title={`Enviar "${doc.label}" al prospecto`}
                      >
                        <FileText className="h-4 w-4 text-red-500 dark:text-red-400 group-hover:text-red-600 dark:group-hover:text-red-300 flex-shrink-0 transition-colors" />
                        <span className="font-semibold text-slate-700 dark:text-slate-100 truncate">{doc.label}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPreviewDoc({ label: doc.label, fileName: doc.fileName, url: docUrl }) }}
                        className="flex items-center justify-center h-8 w-8 flex-shrink-0 border-l border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title={`Vista previa de "${doc.label}"`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )
                })}
              </div>

              <button
                onClick={handlePickFile}
                disabled={sendingDoc || !isHumanMode}
                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                title="Subir un archivo desde tu computadora"
              >
                <Download className="h-3.5 w-3.5 rotate-180" />
                Subir nuevo documento desde mi computadora
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleFileSelected(file)
                }}
              />

              {!isHumanMode && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Activa Modo Manual para enviar documentos
                </p>
              )}
            </div>
          </div>
        )}

        {/* Respuestas rápidas */}
        {showQuickReplies && (
          <div className="px-3 pt-2.5 sm:px-4 animate-in slide-in-from-bottom-2 duration-150">
            <div className="p-2.5 bg-gradient-to-br from-emerald-50/50 to-teal-50/30 dark:from-emerald-950/20 dark:to-teal-950/10 border border-emerald-200/60 dark:border-emerald-800/40 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> Respuestas rápidas
                </span>
                <button onClick={() => setShowQuickReplies(false)} className="text-emerald-400 hover:text-emerald-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-32 overflow-y-auto">
                {QUICK_REPLIES.map((qr, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickReply(qr.text, qr.label)}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[11px] font-medium bg-white/80 dark:bg-slate-800/80 border border-emerald-100 dark:border-emerald-900/40 text-slate-700 dark:text-slate-200 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/20 hover:border-emerald-300 transition-all text-left"
                    title={qr.text}
                  >
                    <span className="text-sm flex-shrink-0">{qr.emoji}</span>
                    <span className="truncate">{qr.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Barra de herramientas + Input */}
        <div className="px-3 pt-1.5 sm:px-4">
          <div className="flex items-center gap-2 mb-1.5">
            <button
              onClick={() => { setShowDocMenu(!showDocMenu); setShowQuickReplies(false) }}
              disabled={sendingDoc}
              className={`px-2.5 py-1.5 rounded-xl transition-all text-[11px] font-semibold flex items-center gap-1.5 border ${showDocMenu ? "bg-red-500 text-white border-red-500 shadow-md shadow-red-200 dark:shadow-red-900/30" : "bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-950/20 hover:border-red-300 shadow-sm"} disabled:opacity-50`}
              title="Enviar documentos"
            >
              {sendingDoc ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <FileText className="h-3.5 w-3.5" />}
              Docs
            </button>
            <button
              onClick={() => { setShowQuickReplies(!showQuickReplies); setShowDocMenu(false) }}
              className={`px-2.5 py-1.5 rounded-xl transition-all text-[11px] font-semibold flex items-center gap-1.5 border ${showQuickReplies ? "bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200 dark:shadow-emerald-900/30" : "bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 hover:border-emerald-300 shadow-sm"}`}
              title="Respuestas rápidas"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Rápidas
            </button>
            <div className="flex-1" />
            {!isHumanMode && <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">Activa Modo Manual para responder</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 pb-2 sm:px-4 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:pb-3">
          <div className="flex-1 relative">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={isHumanMode ? "Escribe un mensaje..." : "Activa modo Manual para escribir"}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              disabled={!isHumanMode}
              className={`pl-4 pr-14 h-10 sm:h-11 rounded-2xl text-[13px] shadow-sm transition-all duration-200 ${isHumanMode ? "bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 focus:border-rose-300 focus:ring-2 focus:ring-rose-500/15 focus:bg-white" : "bg-slate-100 dark:bg-slate-700/50 border-slate-200/50 dark:border-slate-600/50 opacity-60 cursor-not-allowed"}`}
              maxLength={500}
            />
            {text.length > 0 && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 font-medium tabular-nums">{text.length}/500</span>}
          </div>
          <Button onClick={handleSend} disabled={!isHumanMode || !text.trim() || sending} className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl p-0 flex items-center justify-center flex-shrink-0 shadow-lg shadow-rose-900/20 hover:shadow-xl hover:shadow-rose-900/25 transition-all duration-200 disabled:opacity-40 disabled:shadow-none" style={{ backgroundColor: "#7a1b33" }}>
            {sending ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Send className="h-5 w-5 text-white" />}
          </Button>
        </div>
      </div>
    </div>
  )

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && handleSafeClose()}
    >
      <AlertModal {...alertModal} onClose={closeAlert} />
      <ConfirmModal {...confirmModal} onClose={closeConfirm} />

      {/* Modal: vista previa de documento PDF */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-6 animate-in fade-in duration-150"
          onClick={(e) => e.target === e.currentTarget && setPreviewDoc(null)}
        >
          <div className="w-full max-w-4xl h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-rose-50 to-white dark:from-rose-950/30 dark:to-slate-900">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{previewDoc.label}</h3>
                  <p className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">{previewDoc.fileName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={previewDoc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  title="Abrir en pestaña nueva"
                >
                  <Download className="h-3.5 w-3.5" /> Abrir
                </a>
                <button
                  type="button"
                  onClick={() => {
                    const doc = DOCUMENT_CATALOG.find((d) => d.fileName === previewDoc.fileName)
                    if (doc) handleSendDocument(doc)
                    setPreviewDoc(null)
                  }}
                  disabled={sendingDoc || !isHumanMode}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold bg-gradient-to-r from-red-500 to-rose-500 text-white hover:from-red-600 hover:to-rose-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Enviar este documento al prospecto"
                >
                  <Send className="h-3.5 w-3.5" /> Enviar
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                  title="Cerrar"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100 dark:bg-slate-950 overflow-hidden">
              <iframe
                src={`${previewDoc.url}#view=FitH`}
                title={previewDoc.label}
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal: confirmar eliminación de mensaje (estilo WhatsApp "para todos") */}
      {messageToDelete && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && !deletingMessage && setMessageToDelete(null)}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900/60 shadow-2xl shadow-red-500/10 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-5 pt-5 pb-3">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-md shadow-red-200 dark:shadow-red-900/40">
                  <Trash2 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 leading-tight">¿Eliminar mensaje?</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Esta acción no se puede deshacer</p>
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-2.5 mb-3">
                <p className="text-[12px] text-slate-700 dark:text-slate-300 line-clamp-3 leading-relaxed">
                  {messageToDelete.mensaje}
                </p>
              </div>
              <p className="text-[12px] text-slate-600 dark:text-slate-400 leading-relaxed">
                Se eliminará <span className="font-semibold">para todos</span>: del CRM y del chat de WhatsApp del prospecto.
              </p>
              {!messageToDelete.wa_message_id && (
                <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400 leading-relaxed flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Solo se ocultará del CRM. Este mensaje no se puede borrar de WhatsApp porque no tiene ID de envío.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={deletingMessage}
                onClick={() => setMessageToDelete(null)}
                className="flex-1 h-9 text-[12px]"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={deletingMessage}
                onClick={handleConfirmDeleteMessage}
                className="flex-1 h-9 text-[12px] bg-red-600 hover:bg-red-700 text-white"
              >
                {deletingMessage ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent mr-1.5" />
                    Eliminando…
                  </>
                ) : (
                  <>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Eliminar para todos
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: confirmar envío de documento subido */}
      {pendingFile && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={(e) => e.target === e.currentTarget && !sendingDoc && resetPendingFile()}
        >
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-md shadow-red-200 dark:shadow-red-900/40">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Enviar documento</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    {pendingFile.name} · {(pendingFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>

              {!sendingDoc && (
                <>
                  <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">
                    Nombre que verá el prospecto
                  </label>
                  <input
                    type="text"
                    value={customCaption}
                    onChange={(e) => setCustomCaption(e.target.value)}
                    disabled={sendWithoutCaption}
                    placeholder="Ej: Brochure del colegio"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  />
                  <label className="mt-3 flex items-center gap-2 text-[11.5px] text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={sendWithoutCaption}
                      onChange={(e) => setSendWithoutCaption(e.target.checked)}
                      className="h-3.5 w-3.5 rounded accent-red-500"
                    />
                    Enviar sin nombre (solo el PDF)
                  </label>
                </>
              )}

              {sendingDoc && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                    <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-200">
                      {uploadStage || "Procesando…"}
                    </span>
                    <span className="ml-auto text-[11px] font-mono text-slate-500 dark:text-slate-400">
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-500 to-rose-500 transition-all duration-200 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <Button
                type="button"
                variant="ghost"
                onClick={resetPendingFile}
                disabled={sendingDoc}
                className="flex-1 h-9 text-xs"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleConfirmUploadAndSend}
                disabled={sendingDoc}
                className="flex-1 h-9 text-xs bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white gap-1.5 shadow-sm"
              >
                {sendingDoc ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Subiendo…
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de eliminación permanente */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900/60 shadow-2xl shadow-red-500/10 overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-5 pt-5 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Eliminar prospecto</h3>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Esta acción no se puede deshacer</p>
                </div>
              </div>
              <div className="rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/40 p-3">
                <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                  Se eliminará permanentemente a <strong>{lead?.nombre_tutor || "este prospecto"}</strong> junto con <strong>todos sus mensajes, tareas, documentos y datos asociados</strong>.
                </p>
              </div>
            </div>
            <div className="flex gap-2 px-5 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800">
              <Button variant="outline" size="sm" className="flex-1 h-9 text-xs rounded-lg" onClick={() => setShowDeleteConfirm(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                className="flex-1 h-9 text-xs rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                disabled={saving}
                onClick={handleDeleteLead}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Eliminando..." : "Eliminar permanentemente"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative w-full max-w-6xl h-[100dvh] sm:h-[85vh] sm:max-h-[900px] flex flex-col sm:flex-row bg-white dark:bg-slate-900 rounded-none sm:rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 sm:border border-slate-200/50 dark:border-slate-700/50 font-sans">
        <div className="sm:hidden flex items-center justify-between gap-3 px-4 py-2.5 pt-[max(0.75rem,env(safe-area-inset-top))] border-b border-slate-200/80 dark:border-slate-700/80 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg flex-shrink-0">
          <div className="flex rounded-2xl bg-slate-100 dark:bg-slate-800 p-1 flex-1 max-w-[220px]">
            <button type="button" onClick={() => setMobileTab("info")} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 ${mobileTab === "info" ? "bg-white dark:bg-slate-700 shadow-md text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}`}>
              <FileText className="h-3.5 w-3.5" />
              Info
            </button>
            <button type="button" onClick={() => setMobileTab("chat")} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 ${mobileTab === "chat" ? "bg-white dark:bg-slate-700 shadow-md text-slate-900 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}`}>
              <MessageSquare className="h-3.5 w-3.5" />
              Chat
              {messages.length > 0 && <span className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{messages.length}</span>}
            </button>
          </div>
          <button onClick={handleSafeClose} className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 flex-shrink-0 active:scale-90 transition-transform" title="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="sm:hidden flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
          {mobileTab === "info" ? infoPanel : chatPanel}
        </div>

        <div className="hidden sm:flex flex-1 overflow-hidden min-h-0 min-w-0">
          <aside className={`flex-shrink-0 overflow-hidden border-r border-slate-200 dark:border-slate-700 transition-all duration-300 ease-in-out ${isSidebarOpen ? "w-[300px] min-w-[300px] md:w-[340px] md:min-w-[340px] lg:w-[380px] lg:min-w-[380px]" : "w-0 min-w-0 border-none"}`}>
            {infoPanel}
          </aside>
          <main className="flex-1 min-w-0 flex flex-col overflow-hidden min-h-0">
            {chatPanel}
          </main>
        </div>
      </div>

      <TransferLeadModal
        open={showTransferModal}
        onClose={() => { setShowTransferModal(false); setTransferPreselected("") }}
        lead={lead}
        currentUser={user}
        initialToAsesorId={transferPreselected}
        onTransferred={() => {
          setShowTransferModal(false)
          setTransferPreselected("")
        }}
      />
    </div>,
    document.body
  )
}
